import 'react-tooltip/dist/react-tooltip.css';
import { cloneElement, type ComponentProps, type ReactElement } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useHtmlId } from '@shared/hooks/useHtmlId';

type ReactTooltipProps = ComponentProps<typeof ReactTooltip>;

export type TooltipProps = {
  children: ReactElement;
  content: ReactTooltipProps['content'];
  tooltipClassName?: string;
  /** When set, used as the react-tooltip id; when omitted, {@link useHtmlId} with `idPrefix` is used. */
  id?: string;
  /** Passed to {@link useHtmlId} when `id` is omitted. */
  idPrefix?: string;
} & Pick<
  ReactTooltipProps,
  'delayShow' | 'noArrow' | 'place' | 'positionStrategy' | 'variant'
>;

export const Tooltip = ({
  children,
  content,
  tooltipClassName,
  id: explicitId,
  idPrefix = 'tooltip',
  delayShow = 200,
  noArrow = true,
  place = 'top',
  positionStrategy,
  variant = 'dark',
}: TooltipProps) => {
  const generatedId = useHtmlId(idPrefix);
  const tooltipId = explicitId ?? generatedId;

  return (
    <>
      {cloneElement(children, { 'data-tooltip-id': tooltipId })}
      <ReactTooltip
        className={tooltipClassName}
        content={content}
        delayShow={delayShow}
        id={tooltipId}
        noArrow={noArrow}
        place={place}
        positionStrategy={positionStrategy}
        variant={variant}
      />
    </>
  );
};
